import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  ArrowDownLeft,
  RefreshCw,
  Activity,
  ChevronDown,
  ShoppingBag,
  ShoppingCart,
  Coffee,
  Bus,
  Play,
  Zap,
  Dumbbell,
  Plane,
  Landmark,
  PencilLine,
} from "lucide-react";
import { detectMerchant, countMerchants, CATEGORIES } from "@/lib/merchants";

const ICON_MAP = {
  ShoppingBag,
  ShoppingCart,
  Coffee,
  Bus,
  Play,
  Zap,
  Dumbbell,
  Plane,
  Landmark,
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOCALES = { GBP: "en-GB", EUR: "en-IE", USD: "en-US", CAD: "en-CA", AUD: "en-AU" };

const formatAmount = (amount, currency) => {
  try {
    return new Intl.NumberFormat(LOCALES[currency] || "en-GB", {
      style: "currency",
      currency: currency || "GBP",
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency || ""}`.trim();
  }
};

const relTime = (iso) => {
  if (!iso) return "";
  try {
    const t = new Date(iso).getTime();
    const diff = (Date.now() - t) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.round(diff / 86400)}d ago`;
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
};

const TransactionHistory = ({ userId, refreshKey = 0 }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all"); // all | payment | expense
  const [cardFilter, setCardFilter] = useState("all"); // card_id | "all"
  const [sourceFilter, setSourceFilter] = useState("all"); // all | manual | bank
  const [visibleCount, setVisibleCount] = useState(5);
  const INITIAL_COUNT = 5;
  const STEP = 10;

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (userId) params.user_id = userId;
      const res = await axios.get(`${API}/cards/transactions`, {
        params,
        withCredentials: true,
      });
      setItems(res.data?.data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, refreshKey]);

  const cardOptions = useMemo(() => {
    const seen = new Map();
    for (const it of items) {
      if (!seen.has(it.card_id)) seen.set(it.card_id, it.card_name || "Card");
    }
    return Array.from(seen.entries());
  }, [items]);

  useEffect(() => {
    if (cardFilter !== "all" && !cardOptions.some(([id]) => id === cardFilter)) {
      setCardFilter("all");
    }
  }, [cardOptions, cardFilter]);

  const hasBothSources = useMemo(() => {
    let hasManual = false, hasBank = false;
    for (const it of items) {
      if (it.source === "plaid") hasBank = true;
      else hasManual = true;
      if (hasManual && hasBank) return true;
    }
    return false;
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      if (typeFilter !== "all" && it.type !== typeFilter) return false;
      if (cardFilter !== "all" && it.card_id !== cardFilter) return false;
      if (sourceFilter === "manual" && it.source === "plaid") return false;
      if (sourceFilter === "bank" && it.source !== "plaid") return false;
      return true;
    });
  }, [items, typeFilter, cardFilter, sourceFilter]);

  // Reset the pagination window whenever the filtered set changes
  // (filter switch, data refresh) so the user always starts at the 5 newest.
  useEffect(() => {
    setVisibleCount(INITIAL_COUNT);
  }, [typeFilter, cardFilter, sourceFilter, items]);

  const visibleItems = useMemo(
    () => filtered.slice(0, visibleCount),
    [filtered, visibleCount]
  );
  const hasMore = filtered.length > visibleCount;
  const isExpanded = visibleCount > INITIAL_COUNT;

  // Merchant counts across the FULL feed (not just filtered) so the "×3" badge
  // reflects real activity totals, not what happens to be showing.
  const merchantCounts = useMemo(() => countMerchants(items), [items]);

  const showFilters = items.length > 3;
  const emptyLabel = items.length === 0
    ? "No transactions yet — link a bank via Plaid or edit a manual card's balance and they'll appear here."
    : "No matches for these filters — try widening them.";

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay: 0.15 }}
      className="mt-8 rounded-2xl border border-white/5 bg-white/[0.02] px-5 sm:px-6 py-5"
      data-testid="transaction-history-panel"
    >
      <header className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-[#00E676]/10 border border-[#00E676]/30 flex items-center justify-center flex-shrink-0">
            <Activity size={14} className="text-[#00E676]" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#00E676] font-bold">
              Transaction History
            </div>
            <h3
              className="text-base sm:text-lg text-white font-semibold truncate"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Bank activity & manual updates
            </h3>
          </div>
        </div>
        <button
          type="button"
          onClick={fetchItems}
          disabled={loading}
          aria-label="Refresh activity"
          className="text-slate-400 hover:text-white transition-colors disabled:opacity-40 flex-shrink-0"
          data-testid="recent-activity-refresh"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
        </button>
      </header>

      {showFilters && (
        <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="recent-activity-filters">
          {/* Type pill toggle */}
          <div className="inline-flex rounded-full border border-white/10 bg-white/[0.02] p-0.5" role="tablist">
            {[
              { key: "all", label: "All" },
              { key: "payment", label: "Payments" },
              { key: "expense", label: "Expenses" },
            ].map((opt) => {
              const active = typeFilter === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setTypeFilter(opt.key)}
                  data-testid={`activity-filter-type-${opt.key}`}
                  className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] rounded-full transition-colors ${
                    active
                      ? "bg-[#00E676] text-[#060B19]"
                      : "text-slate-300 hover:text-white"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          {/* Card dropdown — only when there's more than one card in the timeline */}
          {cardOptions.length > 1 && (
            <div className="relative">
              <select
                value={cardFilter}
                onChange={(e) => setCardFilter(e.target.value)}
                data-testid="activity-filter-card"
                className="appearance-none cursor-pointer rounded-full border border-white/10 bg-white/[0.02] hover:border-white/30 text-slate-300 hover:text-white px-3 pr-8 py-1.5 text-[11px] font-semibold transition-colors focus:outline-none focus:border-[#00E676]"
              >
                <option value="all">All cards ({cardOptions.length})</option>
                {cardOptions.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={12}
                className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400"
              />
            </div>
          )}

          {/* Source pill toggle — only when both Manual and Bank activity coexist */}
          {hasBothSources && (
            <div className="inline-flex rounded-full border border-white/10 bg-white/[0.02] p-0.5" role="tablist">
              {[
                { key: "all", label: "All sources" },
                { key: "bank", label: "Bank" },
                { key: "manual", label: "Manual" },
              ].map((opt) => {
                const active = sourceFilter === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setSourceFilter(opt.key)}
                    data-testid={`activity-filter-source-${opt.key}`}
                    className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] rounded-full transition-colors ${
                      active
                        ? "bg-[#00E676] text-[#060B19]"
                        : "text-slate-300 hover:text-white"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}

          {(typeFilter !== "all" || cardFilter !== "all" || sourceFilter !== "all") && (
            <button
              type="button"
              onClick={() => {
                setTypeFilter("all");
                setCardFilter("all");
                setSourceFilter("all");
              }}
              data-testid="activity-filter-clear"
              className="text-[11px] text-slate-400 hover:text-white underline underline-offset-2 transition-colors"
            >
              Clear
            </button>
          )}

          <span className="ml-auto text-[11px] text-slate-400" data-testid="activity-filter-count">
            {visibleItems.length} of {filtered.length}
          </span>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="py-4 text-sm text-slate-400" data-testid="recent-activity-empty">
          {loading ? "Loading…" : emptyLabel}
        </div>
      ) : (
        <>
        <ul className="divide-y divide-white/5" data-testid="recent-activity-list">
          {visibleItems.map((it) => {
            const isExpense = it.type === "expense";
            const isPayment = it.type === "payment";
            const isCorrection = it.type === "correction";
            const color = isCorrection ? "#38BDF8" : isExpense ? "#F87171" : "#00E676";
            const Icon = isCorrection ? PencilLine : isExpense ? ArrowUpRight : ArrowDownLeft;
            const label = isCorrection ? "Correction" : isExpense ? "Expense" : "Payment";
            const isBank = it.source === "plaid";
            // Merchant detection for Plaid rows (from description regex).
            // For manual expenses the user picks a category in the dialog —
            // fall back to CATEGORIES[it.category] so the badge + colour match.
            let merchant = isBank ? detectMerchant(it.description) : null;
            if (!merchant && it.category && CATEGORIES[it.category]) {
              merchant = { name: null, category: it.category, ...CATEGORIES[it.category] };
            }
            const primary = merchant?.name || (isBank && it.description ? it.description : it.card_name);
            const secondaryCard = (isBank && (merchant || it.description)) ? it.card_name : null;
            const merchantCount = merchant?.name ? merchantCounts.get(merchant.name) : 0;
            const MerchantIcon = merchant ? ICON_MAP[merchant.icon] : null;
            return (
              <li
                key={it.id}
                className="flex items-center gap-3 py-3"
                data-testid={`transaction-history-item-${it.id}`}
              >
                <span
                  className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: `${color}18`, border: `1px solid ${color}55`, color }}
                >
                  <Icon size={15} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white font-semibold truncate flex items-center gap-2">
                    <span className="truncate">{primary}</span>
                    {merchant && merchantCount > 1 && (
                      <span
                        className="text-[10px] font-semibold px-1.5 py-[1px] rounded-full whitespace-nowrap"
                        style={{
                          color: merchant.color,
                          background: `${merchant.color}18`,
                          border: `1px solid ${merchant.color}55`,
                        }}
                        data-testid={`merchant-count-${merchant.name.toLowerCase().replace(/\s+/g,'-')}`}
                        title={`Seen ${merchantCount} times in this view`}
                      >
                        ×{merchantCount}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                    {merchant && MerchantIcon && (
                      <span
                        className="inline-flex items-center gap-1 rounded-full px-1.5 py-[1px] text-[9px] uppercase tracking-[0.14em] font-bold"
                        style={{
                          color: merchant.color,
                          background: `${merchant.color}18`,
                          border: `1px solid ${merchant.color}55`,
                        }}
                      >
                        <MerchantIcon size={9} />
                        {merchant.label}
                      </span>
                    )}
                    <span style={{ color }}>{label}</span>
                    {secondaryCard && (
                      <>
                        <span>·</span>
                        <span className="truncate">{secondaryCard}</span>
                      </>
                    )}
                    <span>·</span>
                    <span>{relTime(it.at)}</span>
                    <span
                      className="inline-flex items-center rounded-full border px-1.5 py-[1px] text-[9px] uppercase tracking-[0.14em] font-bold"
                      style={
                        isBank
                          ? { color: "#7DD3FC", borderColor: "#38BDF855", background: "#38BDF811" }
                          : { color: "#B7C0D4", borderColor: "#FFFFFF22", background: "#FFFFFF08" }
                      }
                    >
                      {isBank ? "Bank" : "Manual"}
                    </span>
                  </div>
                </div>
                <div
                  className="text-sm font-semibold text-right whitespace-nowrap"
                  style={{ color, fontFamily: "'Outfit', sans-serif" }}
                >
                  {isExpense ? "+" : isCorrection ? "±" : "−"}
                  {formatAmount(it.amount, it.currency).replace(/[+-−±]?/, "")}
                </div>
              </li>
            );
          })}
        </ul>
        {(hasMore || isExpanded) && (
          <div
            className="mt-3 flex items-center justify-center gap-2"
            data-testid="transaction-history-pagination"
          >
            {hasMore && (
              <button
                type="button"
                onClick={() =>
                  setVisibleCount((c) => Math.min(c + STEP, filtered.length))
                }
                data-testid="transaction-history-load-more"
                className="rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] hover:border-white/30 text-slate-200 hover:text-white px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors"
              >
                Load more
                <span className="ml-1.5 text-slate-400 font-normal normal-case tracking-normal">
                  (+{Math.min(STEP, filtered.length - visibleCount)})
                </span>
              </button>
            )}
            {isExpanded && (
              <button
                type="button"
                onClick={() => setVisibleCount(INITIAL_COUNT)}
                data-testid="transaction-history-show-less"
                className="rounded-full border border-white/10 bg-transparent hover:bg-white/[0.04] hover:border-white/30 text-slate-400 hover:text-white px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors"
              >
                Show less
              </button>
            )}
          </div>
        )}
        </>
      )}
    </motion.section>
  );
};

export default TransactionHistory;
